<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import { Card, CardHeader, CardContent, CardFooter } from '$lib/components/ui/card/index.js'
  import { cn } from '$lib/utils.js'
  import { parseOtpFromClipboard, pasteNextFocusIndex } from '$lib/otp-utils.js'

  let { masked_email = '', flash = {} } = $props()

  let authError = $derived(flash.auth_error ?? null)

  let digits = $state(['', '', '', '', '', ''])
  let inputRefs = $state([])
  let submitting = $state(false)

  let code = $derived(digits.join(''))
  let isComplete = $derived(code.length === 6)

  // Auto-submit when all 6 digits are present (works for paste and type-last-digit).
  // Guard: !submitting prevents re-triggering on flash re-render where isComplete is still true.
  $effect(() => {
    if (isComplete && !submitting) {
      handleSubmit(new Event('submit'))
    }
  })

  function handleDigit(index, e) {
    const val = e.target.value.replace(/\D/g, '').slice(-1)
    digits[index] = val
    if (val && index < 5) {
      inputRefs[index + 1]?.focus()
    }
  }

  function handleKeydown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      digits[index - 1] = ''
      inputRefs[index - 1]?.focus()
    }
  }

  function handlePaste(e) {
    e.preventDefault()
    if (submitting) return
    // Always rewrites all 6 boxes from position 0, regardless of which box is focused.
    // Paste of 0 numeric digits leaves existing state unchanged (intentional).
    // iOS autocomplete="one-time-code" uses the input event (not paste), so
    // e.preventDefault() here does not affect iOS OTP autofill suggestions.
    // Note: inputRefs must remain a fixed-length 6-element array — do NOT add
    //       keyed {#each} here or refs will become stale during re-render.
    const parsed = parseOtpFromClipboard(e.clipboardData.getData('text'))
    parsed.forEach((ch, i) => { digits[i] = ch })
    inputRefs[pasteNextFocusIndex(parsed)]?.focus()
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!isComplete || submitting) return
    submitting = true
    router.post('/login/verify', { code }, {
      onFinish: () => { submitting = false }
    })
  }

  function handleResend() {
    router.visit('/login')
  }
</script>

<svelte:head>
  <title>Verify Email — MyRentalTax</title>
</svelte:head>

<div class="min-h-dvh flex flex-col items-center justify-center gap-5 px-5 py-8 bg-[#fafafa]">
  <!-- Logo -->
  <div class="flex items-center gap-3">
    <div class="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-900 rounded-[10px] sm:rounded-xl flex items-center justify-center text-white flex-shrink-0">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>
      </svg>
    </div>
    <span class="text-2xl sm:text-3xl font-bold text-zinc-900">MyRentalTax</span>
  </div>
  <p class="text-[13px] sm:text-base text-zinc-500 -mt-2">Malaysian Rental Income Tax Wizard — YA 2025</p>

  <form class="w-full max-w-sm sm:max-w-[480px]" onsubmit={handleSubmit}>
    <Card class="shadow-sm">
      <CardHeader class="px-5 pt-5 pb-0 sm:px-6 sm:pt-6 gap-1">
        <h2 class="text-lg sm:text-xl font-semibold text-zinc-900 m-0">Verify Your Email</h2>
        <p class="text-sm text-zinc-500 leading-relaxed m-0">
          We sent a 6-digit code to <strong class="text-zinc-700">{masked_email}</strong>. Enter it below to continue.
        </p>
      </CardHeader>

      <CardContent class="px-5 py-5 sm:px-6 flex flex-col gap-4">
        {#if authError === 'invalid_code'}
          <div class="bg-red-50 border border-red-200 rounded-md px-3.5 py-2.5 text-sm text-red-600">
            Incorrect code. Please try again.
          </div>
        {/if}

        <div class="flex flex-col gap-2">
          <label for="otp-input-0" class="text-sm font-medium text-zinc-900">One-Time PIN</label>
          <div class="flex gap-2 w-full" role="group" aria-label="OTP input">
            {#each digits as digit, i}
              <input
                type="text"
                id={i === 0 ? 'otp-input-0' : undefined}
                inputmode="numeric"
                maxlength="1"
                class={cn(
                  "flex-1 h-12 sm:h-[52px] text-center text-lg sm:text-xl font-semibold font-mono min-w-0",
                  "border rounded-md outline-none transition-all",
                  "bg-zinc-50 border-zinc-200 text-zinc-900",
                  "focus:border-zinc-900 focus:bg-white focus:ring-2 focus:ring-zinc-900/8",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  digit !== '' && "bg-white border-zinc-400"
                )}
                value={digit}
                disabled={submitting}
                oninput={(e) => handleDigit(i, e)}
                onkeydown={(e) => handleKeydown(i, e)}
                onpaste={handlePaste}
                bind:this={inputRefs[i]}
                autocomplete="one-time-code"
              />
            {/each}
          </div>
          <!-- Screen reader announcement: only when complete AND no error showing -->
          <div aria-live="polite" class="sr-only">
            {isComplete && !authError ? 'Six-digit code entered. Verifying…' : ''}
          </div>
        </div>

        <div class="flex items-center justify-center gap-1 text-sm">
          <span class="text-zinc-500">Didn't receive the code?</span>
          <button type="button" class="bg-transparent border-none p-0 cursor-pointer text-sm font-medium text-zinc-900 underline underline-offset-2 hover:text-zinc-500 transition-colors" onclick={handleResend}>
            Resend
          </button>
        </div>
      </CardContent>

      <CardFooter class="px-5 pb-5 sm:px-6 sm:pb-6 border-t border-zinc-100 pt-4">
        <Button type="submit" class="w-full" disabled={!isComplete || submitting}>
          {submitting ? 'Verifying…' : 'Verify & Continue'}
        </Button>
      </CardFooter>
    </Card>
  </form>
</div>
